import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Box, Button, Slider, Stack, Typography, Accordion, AccordionSummary, AccordionDetails, useTheme } from '@mui/material'
import Grid from '@mui/material/Grid'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useFormContext, Controller } from 'react-hook-form'
import { shallowEqual, useSelector } from 'react-redux'
import T from 'T'
import { FilterMultiSelectField } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filters'
import { filterDefaultValues } from 'components/configured-service/CreateDraftConfiguredServiceDialog/filterSettings'
import useRadiusUnit from 'components/configured-service/Schedule/hooks/useRadiusUnit'
import ActiveServiceDaysSelector from 'components/configured-service/tabs/MapRoutingTab/components/ActiveServiceDaysSelector'
import RoutesLegends from 'components/configured-service/Schedule/RoutesLegends'
import OverCapacityNotice from 'components/configured-service/Schedule/OverCapacityNotice'
import { SCHEDULING_MAP_SURFACE, selectSchedulingResultsMeta } from 'slices/configuredService/configuredServiceScheduleSlice'

const ScheduleStopFiltersPanel = ({
  businessLineOptions = [],
  materialOptions = [],
  measureOptions = [],
  serviceActionOptions = [],
  expanded = false,
  onExpandedChange,
  showDaysSelector = false,
  showRoutesLegend = false,
  routesLegendData,
  routesLegendLoading = false,
}) => {
  const { control, setValue } = useFormContext()
  const { truncated, totalMatched, cap } = useSelector(state => selectSchedulingResultsMeta(state, SCHEDULING_MAP_SURFACE.SCHEDULE_STOP), shallowEqual)
  const theme = useTheme()

  const { unitLabel, sliderConfig, displayUnitValue, onSliderChange, onSliderCommit, resetRadius } = useRadiusUnit({
    distanceUnitProp: 'mi',
    radiusMinMeters: 10000,
    radiusMaxMeters: 50000,
    radiusStepMeters: 5000,
  })

  const handleClearAll = useCallback(
    e => {
      e.stopPropagation()
      setValue('measure', [])
      setValue('material', [])
      setValue('action', [])
      setValue('businessLine', [])
      resetRadius()
    },
    [setValue, resetRadius]
  )

  const handleAccordionChange = useCallback(
    (_, isExpanded) => {
      if (onExpandedChange) onExpandedChange(isExpanded)
    },
    [onExpandedChange]
  )

  const slotPropsPopper = useMemo(
    () => ({
      popper: {
        sx: { zIndex: theme.zIndex.tooltip },
      },
    }),
    [theme]
  )

  return (
    <>
      <Accordion expanded={expanded} onChange={handleAccordionChange} elevation={0} disableGutters sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" width="100%" pr={1}>
            <Typography variant="h5" color="textSecondary" fontWeight={500}>
              {T.FILTERS}
            </Typography>
            <Button size="small" onClick={handleClearAll}>
              {T.RESET_ALL}
            </Button>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ backgroundColor: theme.palette.background.default }}>
          <Grid container spacing={2}>
            <Grid size={12}>
              <Box sx={{ px: 2 }}>
                <Typography gutterBottom>{`${T.RADIUS} (${unitLabel}) :${displayUnitValue}`}</Typography>
                <Controller
                  name="radius"
                  control={control}
                  defaultValue={filterDefaultValues.radius}
                  render={() => (
                    <Slider
                      value={displayUnitValue}
                      onChange={(e, val) => onSliderChange(e, val)}
                      onChangeCommitted={(e, val) => onSliderCommit(e, val)}
                      min={sliderConfig.min}
                      max={sliderConfig.max}
                      step={sliderConfig.step}
                      shiftStep={sliderConfig.step}
                      marks
                      valueLabelDisplay="auto"
                    />
                  )}
                />
              </Box>
            </Grid>
            {showDaysSelector && (
              <Grid size={12}>
                <ActiveServiceDaysSelector />
              </Grid>
            )}
            {showRoutesLegend && (
              <Grid size={12}>
                <RoutesLegends routes={routesLegendData} isLoading={routesLegendLoading} />
              </Grid>
            )}
            <Grid size={12}>
              <FilterMultiSelectField
                deprecatedLabel={false}
                size="small"
                name="measure"
                control={control}
                label={T.MEASURE}
                options={measureOptions}
                getOptionLabel={o => o.label}
                fullWidth
                slotProps={slotPropsPopper}
              />
            </Grid>
            <Grid size={12}>
              <FilterMultiSelectField
                deprecatedLabel={false}
                size="small"
                name="material"
                control={control}
                label={T.MATERIAL}
                options={materialOptions}
                getOptionLabel={o => o.label}
                fullWidth
                slotProps={slotPropsPopper}
              />
            </Grid>
            <Grid size={12}>
              <FilterMultiSelectField
                deprecatedLabel={false}
                size="small"
                name="action"
                control={control}
                label={T.ACTION}
                options={serviceActionOptions}
                getOptionLabel={o => o.label}
                fullWidth
                slotProps={slotPropsPopper}
              />
            </Grid>
            <Grid size={12}>
              <FilterMultiSelectField
                deprecatedLabel={false}
                size="small"
                name="businessLine"
                control={control}
                label={T.BUSINESS_LINE}
                options={businessLineOptions}
                getOptionLabel={o => o.label}
                fullWidth
                slotProps={slotPropsPopper}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      {/* Outside the accordion on purpose: the filters collapse by default here, and a notice that
          says "reduce radius" is useless if it's hidden along with the radius slider. */}
      {truncated && (
        <Box px={2} pt={1.5}>
          <OverCapacityNotice variant="panel" truncated={truncated} totalMatched={totalMatched} cap={cap} />
        </Box>
      )}
    </>
  )
}

ScheduleStopFiltersPanel.propTypes = {
  businessLineOptions: PropTypes.array,
  materialOptions: PropTypes.array,
  measureOptions: PropTypes.array,
  serviceActionOptions: PropTypes.array,
  expanded: PropTypes.bool,
  onExpandedChange: PropTypes.func,
  showDaysSelector: PropTypes.bool,
  showRoutesLegend: PropTypes.bool,
  routesLegendData: PropTypes.array,
  routesLegendLoading: PropTypes.bool,
}

export default React.memo(ScheduleStopFiltersPanel)
